import { sendToWorker } from "utils/file-upload"
import type { MapRequest, MapResponse } from "./timesheet-map.worker"
import type { AttendanceRecord, ColumnMap } from "./types"

/**
 * Run timesheet row-mapping in a dedicated worker.
 *
 * SRP: this module ONLY moves rows + columnMap → AttendanceRecord[] off the
 * main thread. File parsing belongs to the generic `useFileUpload` hook;
 * this is the timesheet-specific step that follows it.
 *
 * The function (not a hook) shape is intentional — there's no React state
 * to manage here; callers wrap the call in their own loading flag.
 *
 * Usage:
 *   setIsMapping(true)
 *   try {
 *     const records = await mapRowsInWorker(parsedRows, columnMap)
 *     submit(records)
 *   } finally {
 *     setIsMapping(false)
 *   }
 */

const spawnMapWorker = () =>
  new Worker(new URL("./timesheet-map.worker.ts", import.meta.url), {
    type: "module",
  })

export const mapRowsInWorker = (
  rows: Record<string, unknown>[],
  columnMap: ColumnMap
): Promise<AttendanceRecord[]> =>
  sendToWorker<MapResponse, Extract<MapResponse, { type: "map-success" }>>(
    spawnMapWorker,
    { type: "map", rows, columnMap } satisfies MapRequest,
    "map-success"
  ).then((res) => res.records)
